import React, { lazy, Suspense, useState } from 'react';
import { getStage, stageBusy } from '../../lib/runHelpers';
import { Film, Loader2, Play, AlertCircle } from 'lucide-react';

const ShortPlayerDialog = lazy(() => import('./ShortPlayerDialog'));

function formatDuration(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return null;
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const ShortCard = ({ short, onClick }) => {
  const [videoDurationSeconds, setVideoDurationSeconds] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);
  const duration = formatDuration(videoDurationSeconds);
  const failed = short.status && short.status !== 'success';
  const hasUrl = Boolean(short.signedUrl);

  return (
    <button
      type="button"
      onClick={() => hasUrl && !failed && onClick(short)}
      disabled={!hasUrl || failed}
      className="group relative self-start bg-brand-surface rounded-xl overflow-hidden border border-brand-surface-3 hover:border-orange-500/50 transition-all duration-200 hover:scale-[1.03] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 text-left"
    >
      <div className="relative bg-brand-bg overflow-hidden" style={{ aspectRatio }}>
        {hasUrl ? (
          <video
            src={`${short.signedUrl}#t=0.1`}
            preload="metadata"
            muted
            playsInline
            onLoadedMetadata={({ currentTarget: video }) => {
              setVideoDurationSeconds(video.duration);
              if (video.videoWidth > 0 && video.videoHeight > 0) setAspectRatio(video.videoWidth / video.videoHeight);
            }}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-orange-500/20 to-red-500/10" />
        )}

        {hasUrl && !failed && (
          <div className="absolute inset-0 bg-brand-bg/30 group-hover:bg-brand-bg/50 transition-colors duration-200 flex items-center justify-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center shadow-lg opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-200">
              <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white fill-white ml-0.5" />
            </div>
          </div>
        )}

        {failed && (
          <div className="absolute inset-0 bg-brand-bg/70 flex items-center justify-center">
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-500/20 border border-red-500/40 rounded-md">
              <AlertCircle className="w-3 h-3 text-red-400" />
              <span className="text-xs text-red-300 font-medium">Failed</span>
            </div>
          </div>
        )}

        {duration && (
          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-brand-bg/90 backdrop-blur-sm rounded-md text-xs sm:text-sm text-white font-bold">
            {duration}
          </div>
        )}

        <div className="absolute top-2 left-2 px-2 py-1 bg-brand-bg/80 backdrop-blur-sm rounded-md text-sm sm:text-base text-orange-300 font-semibold">
          #{short.shortId}
        </div>
      </div>

      <div className="p-2.5">
        <p
          dir="auto"
          className="text-start text-xs sm:text-sm font-medium text-white line-clamp-2 group-hover:text-orange-400 transition-colors duration-200 leading-snug"
        >
          {short.title || `Short ${short.shortId}`}
        </p>
      </div>
    </button>
  );
};

export const ShortsTab = ({
  run,
  status,
  showCreateButton = false,
  onCreateShorts,
  creatingShortsLoading = false,
  creatingShortsError = null,
  getDownloadLink,
}) => {
  const [shortInModal, setShortInModal] = useState(null);

  const shortsStage = getStage(run, 'shorts');
  const mapping = getStage(run, 'mapping');
  const results = Array.isArray(shortsStage.shortResults) ? shortsStage.shortResults : [];
  const shorts = results.filter(short => short.status === 'success');
  const failedCount = results.filter(short => ['error', 'failed'].includes(short.status)).length;
  const allFailed = shorts.length === 0 && (failedCount > 0 || shortsStage.status === 'failed');
  const shortsStillProcessing = stageBusy(shortsStage) && mapping.status !== 'failed';

  return (
    <>
      <div className="p-4 sm:p-8">
        <div className="flex items-baseline justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-white">Shorts</h2>
          {shorts.length > 0 && (
            <span className="text-sm text-slate-400">
              {shorts.length} {shorts.length === 1 ? 'clip' : 'clips'}
            </span>
          )}
        </div>

        {!shortsStillProcessing && failedCount > 0 && shorts.length > 0 && (
          <p role="status" className="text-sm text-slate-400 mb-4">
            {shorts.length} of {results.length} shorts are ready. {failedCount} couldn’t be created.
          </p>
        )}
        {!shortsStillProcessing && allFailed && (
          <p role="alert" className="text-red-400 mb-4">We couldn’t create your shorts. Please try again.</p>
        )}
        {shorts.length > 0 && !shortsStillProcessing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {shorts.map((s) => (
              <ShortCard
                key={s.shortId ?? Math.random()}
                short={s}
                onClick={setShortInModal}
              />
            ))}
          </div>
        ) : shortsStillProcessing ? (
          <div className="bg-brand-surface rounded-xl p-8 sm:p-12 border border-brand-surface-3 flex flex-col items-center justify-center text-center">
            <Loader2 className="w-10 h-10 text-orange-400 animate-spin mb-3" />
            <p className="text-slate-300">
              {mapping.status !== 'done' ? 'Shorts will start after show notes are ready.' : 'Shorts will appear here when ready'}
            </p>
            <p className="text-slate-500 text-sm mt-1">
              This page updates in real time.
            </p>
          </div>
        ) : showCreateButton ? (
          <div className="bg-brand-surface rounded-xl p-8 sm:p-12 border border-brand-surface-3 flex flex-col items-center justify-center text-center">
            <Film className="w-10 h-10 text-slate-500 mb-3" />
            <p className="text-slate-300 mb-4">
              {mapping.status === 'failed' ? 'Show notes must be retried before shorts can start.' : !run.llmResponse ? 'Creates show notes first, then generates shorts.' : 'Create shorts from your show notes.'}
            </p>
            {creatingShortsError && (
              <p className="text-red-400 text-sm mb-3">{creatingShortsError}</p>
            )}
            <button
              onClick={onCreateShorts}
              disabled={creatingShortsLoading}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:opacity-50 text-white rounded-xl transition-all duration-200 text-sm font-medium"
            >
              {creatingShortsLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Film className="w-4 h-4" />
              )}
              {creatingShortsLoading ? 'Requesting…' : shortsStage.status === 'failed' ? 'Retry Shorts' : mapping.status === 'failed' ? 'Retry Show Notes & Create Shorts' : 'Create Shorts'}
            </button>
          </div>
        ) : !allFailed ? (
          <div className="bg-brand-surface rounded-xl p-8 sm:p-12 border border-brand-surface-3 flex flex-col items-center justify-center text-center">
            <Film className="w-10 h-10 text-slate-500 mb-3" />
            <p className="text-slate-300">No shorts available</p>
          </div>
        ) : null}
      </div>
      {shortInModal && (
        <Suspense fallback={<div role="status" className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center text-white">Loading player…</div>}>
          <ShortPlayerDialog key={shortInModal.shortId} short={shortInModal} onClose={() => setShortInModal(null)} getDownloadLink={getDownloadLink} />
        </Suspense>
      )}
    </>
  );
};
